<script lang="ts" setup>
import {computed, nextTick, shallowRef, useTemplateRef, watch} from 'vue';
import {ChevronLeftIcon, PaperAirplaneIcon, PaperClipIcon, XCircleIcon} from '@heroicons/vue/24/outline';
import {useInternalChatStore} from '@src/stores/internalChat';
import {useAuthStore} from '@src/stores/auth';
import {resolveUrl} from '@src/helpers/utils';
import InternalMessage, {InternalAttachment} from '@src/types/InternalMessage';
import TimelineDivider from '@src/components/views/HomeView/MainArea/ChatArea/TimelineDivider.vue';
import DoubleChecks from '@src/components/ui/icons/DoubleChecks.vue';
import {useIsMobile} from '@src/composables/useIsMobile';

const store = useInternalChatStore();
const auth = useAuthStore();
const {isMobile} = useIsMobile();

const inputText = shallowRef('');
const stagedFile = shallowRef<File | null>(null);
const isLoadingMore = shallowRef(false);

const container = useTemplateRef<HTMLElement>('container');
const fileInput = useTemplateRef<HTMLInputElement>('fileInput');
const textarea = useTemplateRef<HTMLTextAreaElement>('textarea');

const MAX_TEXTAREA_HEIGHT = 96;

const canSend = computed(() =>
    (inputText.value.trim().length > 0 || stagedFile.value !== null) && !store.isSending
);

const initials = computed(() => {
    const name = store.selectedUser?.name ?? '';
    return name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('');
});

const isImage = (attachment: InternalAttachment) =>
    attachment.mime_type.startsWith('image/');

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatTime = (iso: string): string => {
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const isSelf = (msg: InternalMessage): boolean =>
    msg.sender_id === auth.user?.id;

const firstMessageIndices = computed(() => {
    const seen = new Map<string, boolean>();
    const indices = new Set<number>();
    store.messages.forEach((msg, index) => {
        if (msg.created_at) {
            const d = new Date(msg.created_at);
            d.setHours(0, 0, 0, 0);
            const key = d.toISOString();
            if (!seen.has(key)) {
                seen.set(key, true);
                indices.add(index);
            }
        }
    });
    return indices;
});

function scrollToBottom(behavior: ScrollBehavior = 'instant') {
    const el = container.value;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
}

async function handleSend() {
    if (!canSend.value) return;
    const body = inputText.value.trim() || null;
    const file = stagedFile.value;
    inputText.value = '';
    stagedFile.value = null;
    resizeTextarea();
    await store.sendMessage(body, file);
    await nextTick();
    scrollToBottom('smooth');
}

function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey && !isMobile.value) {
        e.preventDefault();
        handleSend();
    }
}

function resizeTextarea() {
    const el = textarea.value;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT) + 'px';
}

function openFileDialog() {
    fileInput.value?.click();
}

function onFileSelected(e: Event) {
    const input = e.target as HTMLInputElement;
    stagedFile.value = input.files?.[0] ?? null;
    input.value = '';
}

function removeStagedFile() {
    stagedFile.value = null;
}

const SCROLL_TOP_THRESHOLD = 120;

async function onScroll() {
    const el = container.value;

    if (!el || isLoadingMore.value || !store.hasMoreMessages) return;

    if (el.scrollTop < SCROLL_TOP_THRESHOLD) {
        isLoadingMore.value = true;
        const previousScrollHeight = el.scrollHeight;
        await store.loadMoreMessages();
        await nextTick();
        el.scrollTop += el.scrollHeight - previousScrollHeight;
        isLoadingMore.value = false;
    }
}

watch(() => store.selectedUserId, async () => {
    await nextTick();
    scrollToBottom();
});

watch(() => store.messages[store.messages.length - 1]?.id, async (newId, oldId) => {
    if (!newId || newId === oldId) return;
    const el = container.value;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 200) {
        await nextTick();
        scrollToBottom('smooth');
    }
});

watch(container, (el) => {
    if (el) el.addEventListener('scroll', onScroll);
});
</script>

<template>
    <!-- empty state -->
    <div
        v-if="!store.selectedUser"
        class="h-full flex items-center justify-center"
    >
        <div class="text-center text-gray-400 dark:text-gray-500">
            <svg class="mx-auto mb-3 w-10 h-10 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" stroke-linecap="round" stroke-linejoin="round"
                    stroke-width="1.5"/>
            </svg>
            <p class="text-sm">Selecione um contato para iniciar a conversa</p>
        </div>
    </div>

    <!-- conversation -->
    <div v-else class="h-full flex flex-col bg-white dark:bg-gray-800">
        <!-- header -->
        <div class="flex-shrink-0 flex items-center gap-3 px-5 py-3 border-b border-gray-100 dark:border-gray-700">
            <button
                class="xs:flex md:hidden items-center justify-center flex-shrink-0 -ml-2 mr-1 p-1.5 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                @click="store.clearSelection()"
            >
                <ChevronLeftIcon class="w-5 h-5"/>
            </button>
            <div
                v-if="store.selectedUser.profile_photo_url"
                :style="{ backgroundImage: `url(${resolveUrl(store.selectedUser.profile_photo_url)})` }"
                class="w-9 h-9 rounded-full bg-cover bg-center bg-gray-200 flex-shrink-0"
            />
            <div
                v-else
                class="w-9 h-9 rounded-full bg-indigo-200 dark:bg-indigo-700 flex items-center justify-center text-xs font-semibold text-indigo-700 dark:text-indigo-200 flex-shrink-0"
            >
                {{ initials }}
            </div>
            <span class="text-sm font-semibold text-slate-800 dark:text-white">
                {{ store.selectedUser.name }}
            </span>
        </div>

        <!-- messages -->
        <div
            ref="container"
            class="flex-1 overflow-y-auto scrollbar-hidden px-5 py-4 flex flex-col gap-2"
        >
            <!-- load more spinner -->
            <div v-if="isLoadingMore" class="flex justify-center py-2">
                <svg class="animate-spin h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" d="M4 12a8 8 0 018-8v8H4z" fill="currentColor"/>
                </svg>
            </div>

            <div v-if="store.isLoadingMessages && !store.messages.length" class="flex justify-center py-8">
                <svg class="animate-spin h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" d="M4 12a8 8 0 018-8v8H4z" fill="currentColor"/>
                </svg>
            </div>

            <!-- message bubbles -->
            <template v-for="(msg, index) in store.messages" :key="msg.id">
            <TimelineDivider v-if="firstMessageIndices.has(index)" :message="msg" />
            <div
                :class="isSelf(msg) ? 'items-end' : 'items-start'"
                class="flex flex-col"
            >
                <div
                    :class="[
                        isSelf(msg)
                            ? 'bg-indigo-500 text-white rounded-tl-2xl rounded-bl-2xl rounded-tr-sm'
                            : 'bg-gray-100 dark:bg-gray-700 text-slate-800 dark:text-white rounded-tr-2xl rounded-br-2xl rounded-tl-sm'
                    ]"
                    class="max-w-xs lg:max-w-md px-4 py-2 rounded-b-2xl"
                >
                    <!-- text body -->
                    <p v-if="msg.body" class="text-sm whitespace-pre-wrap break-words">{{ msg.body }}</p>

                    <!-- attachment -->
                    <div v-if="msg.attachment" class="mt-1">
                        <img
                            v-if="isImage(msg.attachment)"
                            :alt="msg.attachment.original_name"
                            :src="msg.attachment.url"
                            class="max-w-[200px] rounded-lg cursor-pointer"
                        />
                        <a
                            v-else
                            :href="msg.attachment.url"
                            class="flex items-center gap-2 text-xs underline opacity-90"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            <PaperClipIcon class="w-3.5 h-3.5 flex-shrink-0"/>
                            <span class="truncate max-w-[160px]">{{ msg.attachment.original_name }}</span>
                            <span class="opacity-70 flex-shrink-0">{{ formatFileSize(msg.attachment.file_size) }}</span>
                        </a>
                    </div>

                    <!-- timestamp + receipt -->
                    <div class="flex items-center justify-end gap-1 mt-1">
                        <span
                            :class="isSelf(msg) ? 'text-indigo-100' : 'text-gray-400 dark:text-gray-400'"
                            class="text-[10px] leading-none"
                        >
                            {{ formatTime(msg.created_at) }}
                        </span>
                        <DoubleChecks v-if="isSelf(msg)" :state="msg.read_at ? '4' : '2'" />
                    </div>
                </div>
            </div>
            </template>
        </div>

        <!-- input area -->
        <div class="flex-shrink-0 border-t border-gray-100 dark:border-gray-700 px-4 py-3">
            <!-- staged file preview -->
            <div v-if="stagedFile" class="flex items-center gap-2 mb-2 px-1">
                <PaperClipIcon class="w-4 h-4 text-gray-500 flex-shrink-0"/>
                <span class="text-xs text-gray-600 dark:text-gray-300 truncate flex-1">{{ stagedFile.name }}</span>
                <button class="text-gray-400 hover:text-gray-600 flex-shrink-0" @click="removeStagedFile">
                    <XCircleIcon class="w-4 h-4"/>
                </button>
            </div>

            <div class="flex items-end gap-2">
                <!-- file attach -->
                <button
                    class="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors mb-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 cursor-pointer"
                    title="Anexar arquivo"
                    @click="openFileDialog"
                >
                    <PaperClipIcon class="w-5 h-5 text-gray-400"/>
                </button>
                <input ref="fileInput" class="hidden" type="file" @change="onFileSelected"/>

                <!-- textarea -->
                <div class="flex-1 border border-gray-300 dark:border-gray-600 rounded-2xl px-4 py-2 focus-within:border-indigo-400 transition-colors bg-white dark:bg-gray-900">
                    <textarea
                        ref="textarea"
                        v-model="inputText"
                        class="w-full resize-none bg-transparent outline-none border-0 focus:ring-0 text-sm placeholder:text-gray-400 dark:text-white"
                        :placeholder="isMobile ? 'Escreva uma mensagem...' : 'Escreva uma mensagem... Enter para enviar, Shift+Enter para nova linha'"
                        rows="1"
                        @input="resizeTextarea"
                        @keydown="handleKeydown"
                    />
                </div>

                <!-- send button -->
                <button
                    :class="canSend ? 'bg-indigo-500 hover:bg-indigo-600 cursor-pointer' : 'bg-gray-200 dark:bg-gray-700 cursor-not-allowed'"
                    :disabled="!canSend"
                    class="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors mb-0.5"
                    @click="handleSend"
                >
                    <PaperAirplaneIcon
                        :class="canSend ? 'text-white' : 'text-gray-400'"
                        class="w-5 h-5"
                    />
                </button>
            </div>
        </div>
    </div>
</template>
